/**
 * useRail.ts
 */
import Focus from '@shared/types/enums/focus';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { selectMagicRemoteEnabled } from '@shared/store/slices/magicRemote.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useState } from 'react';
import {
  selectCurrentRailVIndex,
  selectHIndex,
  selectSelection,
  selectVIndex,
} from '@shared/store/slices/main.slice';

/**
 * Hook that returns required values and callbacks for a rail.
 * Reads its own layout entry (and the current rail's vIndex) directly from
 * the main slice, via id-scoped selectors, so a rail only re-renders when
 * its own hIndex/vIndex actually changes - not on every other rail's
 * navigation.
 */
export default function useRail(id: string, focus: Focus) {
  const dispatch = useAppDispatch();
  const [hovered, setHovered] = useState(false);
  const enabled = useAppSelector(selectMagicRemoteEnabled);
  const focusSlice = useAppSelector(selectFocus);
  const selection = useAppSelector(selectSelection);
  const hIndex = useAppSelector(selectHIndex(id));
  const vIndex = useAppSelector(selectVIndex(id));
  const currentRail = useAppSelector(selectCurrentRailVIndex);

  useEffect(() => {
    if (!enabled) {
      setHovered(false);
    }
  }, [enabled]);

  return {
    focused: !enabled && focusSlice === focus && selection === id,
    hovered,
    hIndex,
    vIndex,
    currentRail,
    callbacks: {
      onMouseEnter: () => {
        if (focusSlice === Focus.FLOATING_MENU) return;
        dispatch(setFocus(focus));
        setHovered(true);
      },
      onMouseLeave: () => {
        setHovered(false);
      },
    },
  };
}
